import React, { useCallback, useEffect, useState, FC, useMemo } from 'react';
import StudyCardFooter from './components/footer/StudyCardFooter';
import { StudyCardHeader } from './components/header/StudyCardHeader';
import { CardsList } from './components/cards-list/CardsList';
import {
  StudyCardContainer,
  StudyCardContent,
  StudyCardNewFooter,
} from './components/atoms/styles';
import { TrainingSession } from './components/training-session/TrainingSession';
import { useAppDispatch, useAppSelector } from '../shared/hooks/useAppDispatch';
import {
  StudyCardsTypes,
  useGetNextCardMutation,
  useGetSessionScoreMutation,
  useSendCardScoreMutation,
} from '../shared/slices/study-card/api/studyCard';
import { LogActionTypes, useDataLogging } from '../shared/data-logging';
import {
  resetCardsViewed,
  resetSessionScore,
  resetTrainingSessionId,
  resetCardScore,
  setTrainingStarted,
  setTrainingParams,
  setCurrentCard,
  setCardScore,
} from '../shared/slices/study-card/entity/StudyCardSlice';
import { StudyCardWithScore } from '../shared/slices/study-card/types/cards';
import {
  ITrajectoryDetail,
  trajectorySectionCodeLookup,
  trajectorySectionorWorksheetTypeCodeLookup,
} from 'src/config/api/models/Trajectory';
import { ScoreDialog } from './components/score-dialog/ScoreDialog';
import { useSnackbar } from 'notistack';
import {
  SidePanelModes,
  changePanelContent,
  openSidePanel,
} from '../shared/slices/sidePanel/sidePanelSlice';
import { useUser } from '../shared';
import { useAddPageMutation, useGetPagesQuery } from '../shared/slices/page/api/pages';
import { TrajectoryViewModeContent } from '../trajectory/components/Differentiation/TrajectoryViewModeContent';

type StudyCardProps = {
  trajectory?: ITrajectoryDetail;
};

type CardList = {
  title: string;
  subtitle: string;
  type: StudyCardsTypes;
};

const cardList: CardList[] = [
  {
    title: 'Onthoudvragen',
    subtitle: 'Antwoord in je eigen woorden.',
    type: 'study_question',
  },
  {
    title: 'Begrippen',
    subtitle: 'Antwoord met de juiste term of definitie.',
    type: 'term',
  },
  {
    title: 'Woordenschat',
    subtitle: 'Antwoord met een vertaling (en eventueel verklaring en/of voorbeeldzin).',
    type: 'vocabulary',
  },
];

export const StudyCard: FC<StudyCardProps> = ({ trajectory }) => {
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const trainingSessionId = useAppSelector((state) => state.studyCard.trainingSessionId);
  const currentCard = useAppSelector((state) => state.studyCard.currentCard);
  const accumulatedScore = useAppSelector((state) => state.studyCard.sessionScore);
  const cardScore = useAppSelector((state) => state.studyCard.cardScore);
  const trainingStarted = useAppSelector((state) => state.studyCard.trainingStarted);
  const trainingParams = useAppSelector((state) => state.studyCard.trainingParams);
  const bookId = useAppSelector((state) => state.trajectories.currentBookId);
  const trajectoryId = useAppSelector((state) => state.trajectories.currentTrajectoryId);
  const trajectories = useAppSelector((state) => state.trajectories.trajectory);
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const [sessionScore, setSessionScore] = useState<StudyCardWithScore[]>([]);
  const [flipCard, setFlipCard] = useState<boolean>(false);
  const [frontside, setFrontSide] = useState(trainingParams?.isReverseMode !== true);
  const { enqueueSnackbar } = useSnackbar();
  const { isStudent } = useUser();

  const [addPage, addPageResponse] = useAddPageMutation();
  const [getSessionScore] = useGetSessionScoreMutation();
  const [sendCardScore] = useSendCardScoreMutation();
  const [getNextCard] = useGetNextCardMutation();

  const { data: pageData } = useGetPagesQuery({
    trajectoryId: trajectoryId!,
    type: trajectorySectionCodeLookup['3'],
  });

  const pageId = useMemo(() => {
    const id = pageData?.find((item) => item.type === trajectorySectionCodeLookup['3'])?.id;
    if (id) return id;

    return addPageResponse.data?.id;
  }, [pageData, addPageResponse]);

  useEffect(() => {
    if (isStudent || !bookId || !trajectoryId) return;
    if (!pageData) return;

    if (pageId) return;

    addPage({
      bookId,
      trajectoryId,
      type: trajectorySectionorWorksheetTypeCodeLookup['3'],
    });
  }, [isStudent, bookId, trajectoryId, pageData, addPage, pageId]);

  useEffect(() => {
    log(LogActionTypes.OPEN_TRAINER);

    return () => {
      dispatch(resetCardScore());
    };
  }, [dispatch, log]);

  const handleStopTraining = useCallback(
    (getScore?: boolean) => {
      log(LogActionTypes.STOP_PRACTICE, {
        pageId,
        trainingSessionId,
      });
      if (getScore && trainingSessionId) getSessionScore({ trainingSessionId: trainingSessionId! });

      dispatch(resetSessionScore());
      setSessionScore([]);
      dispatch(setTrainingStarted(false));
      dispatch(
        setTrainingParams({ isReverseMode: false, onlyUnknownCards: false, resetAllCards: false })
      );
      setFlipCard(false);
      dispatch(resetTrainingSessionId());
    },
    [log, pageId, trainingSessionId, getSessionScore, dispatch]
  );

  function handleFlipCard(sendLoggingEvent: boolean = true) {
    setFlipCard(!flipCard);
    setFrontSide(!frontside);

    if (!sendLoggingEvent) return;
    log(LogActionTypes.FLIP_CARD, {
      studyCardId: currentCard?.id,
      trainingSessionId,
    });
  }

  useEffect(() => {
    if (cardScore?.isKnown && trainingSessionId) {
      log(LogActionTypes.CARD_KNOWN, {
        studyCardId: cardScore?.id,
        trainingSessionId,
      });
      enqueueSnackbar("3 keer op rij 'juist'.\nJe kent dit kaartje. Het wordt niet meer getoond.", {
        variant: 'success',
        style: { whiteSpace: 'pre-line' },
      });
    }
  }, [enqueueSnackbar, cardScore?.isKnown, cardScore?.id, log, trainingSessionId]);

  function handleCardScore(isCorrect?: boolean) {
    log(isCorrect ? LogActionTypes.CARD_CORRECT : LogActionTypes.CARD_TRY_AGAIN, {
      studyCardId: currentCard?.id,
      trainingSessionId,
    });
    sendCardScore({
      studyCardId: currentCard?.id as string,
      isCorrect,
      trainingSessionId: trainingSessionId as string,
    }).then((t: any) => {
      dispatch(setCardScore(t?.data?.data));
      getNextCard({
        trainingSessionId: trainingSessionId as string,
        lastStudyCard: currentCard?.id,
      }).then((r: any) => {
        if (r?.data === null) {
          handleStopTraining(true);
        }
        dispatch(setCurrentCard(r?.data));
        if (r?.data?.id)
          log(LogActionTypes.SHOW_CARD, { studyCardId: r?.data?.id, trainingSessionId });
      });

      setSessionScore((prev) => [...prev, { ...currentCard!, isCorrect }]);
      handleFlipCard(false);
    });
  }

  useEffect(() => {
    return () => {
      handleStopTraining(false);
    };
  }, [handleStopTraining]);

  return (
    <>
      {isStudent && (
        <ScoreDialog
          score={accumulatedScore!}
          isOpen={accumulatedScore !== undefined}
          onClose={() => {
            dispatch(resetSessionScore());
            dispatch(resetCardsViewed());
          }}
        />
      )}
      <StudyCardContainer id="study-cards-container">
        <StudyCardContent>
          <StudyCardHeader
            trainingStarted={trainingStarted}
            onStopTraining={() => handleStopTraining(true)}
            title={trajectory?.parentTitle ? trajectory?.parentTitle : trajectory?.title}
          />

          {trajectories && <TrajectoryViewModeContent trajectory={trajectories} />}
          {!trainingStarted ? (
            <>
              {cardList.map((item) => {
                return (
                  <CardsList
                    key={item.title}
                    title={item.title}
                    subtitle={item.subtitle}
                    type={item.type}
                    onOpenForm={() => {
                      dispatch(changePanelContent(SidePanelModes.TRAINER_FORM));
                      dispatch(openSidePanel(true));
                    }}
                  />
                );
              })}
            </>
          ) : null}
          {trainingStarted ? (
            <TrainingSession flipCard={flipCard} frontside={frontside} score={sessionScore} />
          ) : null}
        </StudyCardContent>
        {viewMode === 'read' && (
          <StudyCardNewFooter>
            <StudyCardFooter
              onFlipCard={handleFlipCard}
              trainingStarted={trainingStarted}
              flipCard={flipCard}
              onAcceptAnswer={handleCardScore}
            />
          </StudyCardNewFooter>
        )}
      </StudyCardContainer>
    </>
  );
};

export default StudyCard;
